import { Injectable, inject } from '@angular/core';
import { CommonService, TranslationService } from '@waseel-asp/tpa-waseel-lib-staging';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { LOCAL_STORAGE } from '../../constants/localstorage-enum';
import { LanguageType } from '../../constants/language-eum';
import { MatDialogRef } from '@angular/material/dialog';
import { SYSTEM_CONST } from '../../constants/system-constants.model';
import { SettingService } from '../settings/setting.service';
import { SettingsName } from '../../constants/settings-type-enum';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private translation = inject(TranslationService);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private settingService = inject(SettingService);

  private languageReadySubject = new BehaviorSubject<boolean>(false);
  private reloadWindowSubject = new BehaviorSubject<boolean>(false);
  private reloadAllTabsEvent = new Subject<LanguageType>();
  private displayDiffLanguageEvent = new Subject<boolean>();
  private closeLanguageDialogEvent = new Subject<boolean>();
  languageReady$ = this.languageReadySubject.asObservable();
  reloadWindow$ = this.reloadWindowSubject.asObservable();
  reloadAllTabsEvent$ = this.reloadAllTabsEvent.asObservable();
  displayDiffLanguageEvent$ = this.displayDiffLanguageEvent.asObservable();
  closeLanguageDialogEvent$ = this.closeLanguageDialogEvent.asObservable();
  dialogRef: MatDialogRef<any> = null;

  loadLocale(currentLanguage: string) {
    registerLocaleData(currentLanguage === 'ar' ? localeAr : localeEn);
  }

  getLanguage(): string {
    return this.translation.getCurrentLanguage();
  }

  setLanguage(language: LanguageType, removeStorage: boolean, reloadPage: boolean, setTimeoutTime?: number) {
    removeStorage && this.authService.removeItemsFromLocalStoage();
    this.loadLocale(language);
    this.translation.currentLanguage.set(language);
    localStorage.setItem(LOCAL_STORAGE.CURRENT_LANGUAGE, language);
    if (reloadPage) {
      if (setTimeoutTime) {
        setTimeout(() => window.location.reload(), setTimeoutTime);
      } else {
        window.location.reload();
      }
    }
  }

  setLanguageReady(ready: boolean) {
    this.languageReadySubject.next(ready);
  }

  setReloadWindow(shouldReload: boolean) {
    this.reloadWindowSubject.next(shouldReload);
  }

  displayConfirmLanguageDialog(needToBroadcast: boolean) {
    const authDetails = this.settingService.getUserSettingByKey(SettingsName.LanguagePreference);
    const systemLanguageValue = this.settingService.getValueFromSetting(authDetails);
    const storageLanguageValue = localStorage.getItem(LOCAL_STORAGE.CURRENT_LANGUAGE);

    const currentLanguage = this.translation.instant(`COMMON.LANGUAGES.${LanguageType.getDescription(storageLanguageValue).toUpperCase()}`);
    const preferredLanguage = this.translation.instant(`COMMON.LANGUAGES.${LanguageType.getDescription(systemLanguageValue).toUpperCase()}`);

    this.dialogRef = this.commonService.confirmDialodMessage({
      displayOnTop: true,
      title: this.translation.instant(SYSTEM_CONST.CONFIRM),
      message: this.translation.instant('COMMON.LANGUAGES.CONFIRM_LANGUAGE_CHANGE', { currentLanguage: currentLanguage, preferredLanguage: preferredLanguage }),
      primaryButtonCallback: () => this.reloadAllTabsEvent.next(systemLanguageValue as LanguageType),
      secondaryButtonCallback: () => this.closeLanguageDialog(true)
    });
    if (needToBroadcast)
      this.displayDiffLanguageEvent.next(needToBroadcast);
  }

  closeLanguageDialog(needToBroadcast: boolean) {
    this.dialogRef?.close();
    needToBroadcast && this.closeLanguageDialogEvent.next(true);
  }
}
